/**
 * 交通費の確認・確定(2026-09-22 発注者指示。D-128)。日報/サマリの上に出す。
 *  - 出勤(品川)の実稼働が **月100時間以上** になった人を「要確認」として出す
 *  - 管理者が「70%で確定」を押すと、その月の全日の交通費を70%に書き換える(取り消しで元に戻る)
 *  - 確定していない人は 100時間を超えても 0/30/50% のまま(自動では書き換えない)
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import AlertPanel, { type AlertItem } from '@core/components/ui/AlertPanel';
import Panel from '@core/components/ui/Panel';
import StatusBadge from '@core/components/ui/StatusBadge';
import type { MonthStr } from '@core/lib/types';
import { confirmCommute, fetchCommuteCandidates, unconfirmCommute } from '@core/import/commuteConfirmRepo';
import type { CommuteCandidate } from '@core/import/commuteSettle';
import { formatCount, formatCurrency, formatDecimal } from '@core/utils/formatters';

export default function CommuteConfirmPanel({ month, onChanged }: { month: MonthStr; onChanged?: () => void }) {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [list, setList] = useState<CommuteCandidate[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await fetchCommuteCandidates(month));
    } catch {
      setList([]);
    }
  }, [month]);

  useEffect(() => {
    void load();
  }, [load]);

  if (list.length === 0) return null;
  const pending = list.filter((c) => !c.confirmed);
  const monthLabel = `${month.slice(0, 4)}年${Number(month.slice(5))}月`;

  const act = async (c: CommuteCandidate, confirm: boolean) => {
    if (!user) return;
    const msg = confirm
      ? `${c.name} さんの ${monthLabel} の交通費を 70% で確定します。\n${formatCount(c.days, { unit: '日' })}ぶん ${formatCurrency(c.current)} → ${formatCurrency(c.at70)} に書き換えます。よろしいですか？`
      : `${c.name} さんの 70% の確定を取り消し、0/30/50% に戻します。よろしいですか？`;
    if (!window.confirm(msg)) return;
    setBusy(c.key);
    try {
      const who = { id: user.id, name: user.name ?? '' };
      const n = confirm ? await confirmCommute(month, c, who) : await unconfirmCommute(month, c, who);
      toast(confirm ? `70%で確定しました(${n}日ぶんを書き換え)` : `確定を取り消しました(${n}日ぶんを戻しました)`);
      await load();
      onChanged?.();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(null);
    }
  };

  const alerts: AlertItem[] =
    pending.length > 0
      ? [
          {
            id: 'commute',
            severity: 'warning',
            target: `交通費 ${formatCount(pending.length, { unit: '名' })}`,
            message: `${monthLabel}の出勤の実稼働が100時間を超えました。確認して70%で確定してください`,
            diff: pending.map((c) => `${c.name} ${formatDecimal(c.hours, { digits: 1 })}h`).join(' ／ '),
            action: { label: open ? '閉じる' : '確認する', onClick: () => setOpen((v) => !v) },
          },
        ]
      : [];

  return (
    <>
      {alerts.length > 0 && <AlertPanel title="交通費の確認" meta={monthLabel} items={alerts} />}
      {(open || pending.length === 0) && (
        <Panel
          title="交通費の確認(月100時間以上)"
          meta={monthLabel}
          note="出勤の実稼働が月100時間を超えた人は、その月に出た全日の交通費が70%になります。**確定するまでは 0/30/50% のまま**で、確定するとその月の全日を書き換えます(取り消しで元に戻せます)"
        >
          <div className="report-scroll is-auto">
            <table className="report-table">
              <caption className="sr-only">交通費の確認</caption>
              <thead>
                <tr>
                  <th scope="col">スタッフ</th>
                  <th scope="col" className="is-num">月の実稼働</th>
                  <th scope="col" className="is-num">日数</th>
                  <th scope="col" className="is-num">いまの交通費</th>
                  <th scope="col" className="is-num">70%の場合</th>
                  <th scope="col" className="is-num">差額</th>
                  <th scope="col">状態</th>
                  {isAdmin && <th scope="col" />}
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.key}>
                    <th scope="row">{c.name}</th>
                    <td className="is-num">{formatDecimal(c.hours, { digits: 1 })}h</td>
                    <td className="is-num">{formatCount(c.days)}</td>
                    <td className="is-num">{formatCurrency(c.current)}</td>
                    <td className="is-num">{formatCurrency(c.at70)}</td>
                    <td className="is-num">{formatCurrency(c.at70 - c.current)}</td>
                    <td>
                      {c.confirmed ? (
                        <StatusBadge kind="confirmed" label="70%で確定済み" />
                      ) : (
                        <StatusBadge kind="unconfirmed" label="要確認" />
                      )}
                      {c.confirmed && c.hours < 100 && <span className="ui-hint"> 100時間を下回っています</span>}
                    </td>
                    {isAdmin && (
                      <td>
                        {c.confirmed ? (
                          <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" disabled={busy !== null} onClick={() => void act(c, false)}>
                            確定を取り消す
                          </button>
                        ) : (
                          <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" disabled={busy !== null} onClick={() => void act(c, true)}>
                            {busy === c.key ? '保存中…' : '70%で確定'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </>
  );
}
